import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import { Box, Button, Typography } from '@mui/material';
import { reloadOnceForStaleChunk } from '@utils/chunkReload';
import { logger } from '@utils/logger';
import { useEffect } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';

// Vite/browser error text varies slightly across browsers, but always
// references a dynamically imported module failing to load.
const CHUNK_ERROR_PATTERN = /fetch dynamically imported module|error loading dynamically imported module|importing a module script failed/i;

const isChunkLoadError = (error: unknown): boolean =>
  CHUNK_ERROR_PATTERN.test(error instanceof Error ? error.message : String(error));

export const RouteErrorBoundary = () => {
  const error = useRouteError();
  const isStaleChunk = isChunkLoadError(error);

  useEffect(() => {
    if (isStaleChunk) reloadOnceForStaleChunk();
  }, [isStaleChunk]);

  if (isStaleChunk) return null;

  logger.error('Unhandled route error', error, 'RouteErrorBoundary');

  const message = isRouteErrorResponse(error)
    ? error.statusText || `Error ${error.status}`
    : error instanceof Error
      ? error.message
      : 'An unexpected error occurred.';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 6, textAlign: 'center' }}>
      <ErrorOutlineIcon color="error" sx={{ fontSize: 64, mb: 2 }} />
      <Typography variant="h5" gutterBottom>
        Something went wrong
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {message}
      </Typography>
      <Button variant="contained" onClick={() => window.location.reload()}>
        Reload
      </Button>
    </Box>
  );
};
